import { useState, useEffect, createContext } from 'react';
import { Activity, ActivityAddon, ActivityItem, ActivityCategory, ActivityIntervalPriceAdjustment, ActivitySeason, ActivitySeasonTypes } from "interfaces/ActivityInterfaces";
import { BusinessConfig, AgeGroupDetails, CurrencyOption, CountryOption, ActivityRecommendation, RequestParams } from "interfaces/BusinessInterfaces";
import axios from 'axios';
import { useLocalization } from 'hooks/useLocalization';
import { useUI } from 'hooks/useUI';
import { Box, Typography, Alert } from '@mui/material';
import CenteredContent from 'components/shared/CenteredContent';
import NotEmbeddedHeader from 'components/general/NotEmbeddedHeader';
import PaddingWrap from 'components/shared/PaddingWrap';
import { stringsToFetch } from 'utils/stringsToFetch';

interface BusinessContextType {
	getBizConfigVar: <T extends keyof BusinessConfig>(key: T, defaultValue?: BusinessConfig[T]) => BusinessConfig[T];
	activities: Activity[];
	activityAddons: ActivityAddon[];
	activityCategories: ActivityCategory[];
	activityItems: ActivityItem[];
	activityIntervalPriceAdjustments: ActivityIntervalPriceAdjustment[];
	getActivitySeasons: (agentId?: number, isBusiness?: boolean) => ActivitySeason[];
	activityRecommendationsCleaned: ActivityRecommendation[];
	currencyOptions: CurrencyOption[];
	countryOptions: CountryOption[];
	getCurrency: (currencyCode: string) => CurrencyOption|undefined;
	getExchangeFactor: (itemCurrencyCode: string, fromCurrencyCode?: string) => number;
	businessConfig: BusinessConfig;
	businessConfigError: string|null;
	isBusinessLoaded: boolean;
	sendRequest: (params: RequestParams) => void;
	setPageTitle: (title: string, noAddBizName?: boolean) => void;
	businessSlug: string;
	businessToken: string;
}

const defaultBusinessConfig: BusinessConfig = {
	name: 'Default Business',
	logo: {
		hasLogo: false,
		url: null,
		dimensions: {
			width: 0,
			height: 0
		}
	},
	currency: 'USD',
	countryCode: 'US',
	useLodgings: false,
	usePickupLocations: false,
	pickupLocations: [],
	paxPickup: {
		mode: 0,
		useCollectLodging: false
	},
	privateTourMinPax: 1,
	usesAgeGroups: false,
	ageGroups: [],
	languageOptions: [],
	clientDataCustom: [],
	paxListSetup: [],
	hideInfo: {
		arrivalDeparture: false
	},
	clientDataStd: {},
	colors: {
		headerBgsBtns: '1976d2',
		headerTextColor: 'ffffff'
	},
	useSeparateCategories: false,
	layout: 'cards',
	appRootUrlToUse: '',
	bizContactEmails: []
};

const defaultContextValue: BusinessContextType = {
	getBizConfigVar: () => { throw new Error('getBizConfigVar not implemented') },
	activities: [],
	activityAddons: [],
	activityCategories: [],
	activityItems: [],
	activityIntervalPriceAdjustments: [],
	getActivitySeasons: () => [],
	activityRecommendationsCleaned: [],
	currencyOptions: [],
	countryOptions: [],
	getCurrency: () => undefined,
	getExchangeFactor: () => 1,
	businessConfig: defaultBusinessConfig,
	businessConfigError: null,
	isBusinessLoaded: false,
	sendRequest: () => { throw new Error('sendRequest not implemented') },
	setPageTitle: () => { throw new Error('setPageTitle not implemented') },
	businessSlug: '',
	businessToken: ''
};

const BusinessContext = createContext<BusinessContextType>(defaultContextValue);

interface BusinessProviderProps {
	lang: string,
	businessSlug: string,
	businessToken: string,
	appRootUrl: string,
	limitedCategoryIds: number[],
	children: React.ReactNode;
}

export const BusinessProvider: React.FC<BusinessProviderProps> = ({
	lang,
	businessSlug,
	businessToken,
	appRootUrl,
	limitedCategoryIds,
	children
}) => {
	const [isBusinessLoaded, setIsBusinessLoaded] = useState<boolean>(false);
	const [businessConfig, setBusinessConfig] = useState<BusinessConfig>(defaultBusinessConfig);
	const [businessConfigError, setBusinessConfigError] = useState<string|null>(null);
	const [activities, setActivities] = useState<Activity[]>([]);
	const [activityAddons, setActivityAddons] = useState<ActivityAddon[]>([]);
	const [activityCategories, setActivityCategories] = useState<ActivityCategory[]>([]);
	const [activityIntervalPriceAdjustments, setActivityIntervalPriceAdjustments] = useState<ActivityIntervalPriceAdjustment[]>([]);
	const [activitySeasons, setActivitySeasons] = useState<ActivitySeasonTypes | null>(null);
	const [currencyOptions, setCurrencyOptions] = useState<CurrencyOption[]>([]);
	const [countryOptions, setCountryOptions] = useState<CountryOption[]>([]);
	const [activityItems, setActivityItems] = useState<ActivityItem[]>([]);
	const [activityRecommendations, setActivityRecommendations] = useState<ActivityRecommendation[]>([]);
	const { isEmbedded } = useUI();

	const { setStrings, setLangCode } = useLocalization();

	const activityRecommendationsCleaned = activityRecommendations.filter(activityRecommendation => activities.some(activity => activity.id === activityRecommendation.activityId));

	const setPageTitle = (title: string, noAddBizName: boolean = false) => {
		if (isBusinessLoaded) {
			const businessName = getBizConfigVar('name');
			document.title = title + (noAddBizName ? '' : (' | ' + businessName));
		} else {
			document.title = title;
		}
	};
	
	const sendRequest = async ({ type, params, onSuccess, onError, onFinally }: RequestParams) => {
		const baseUrlRemote = 'https://api.tourtask.com/public/v1/';
		const baseUrlLocal = 'http://api.localhost/public/v1/';

		try {
			const response = await axios.post(baseUrlRemote + type, {
				bizSlug: businessSlug,
				bizToken: businessToken,
				langCode: lang,
				params: params
			});
	
			if (response.data[type]) { //Success
				onSuccess(response.data);
			} else if (response.data.error) { //Clean error
				console.log('clean error', response);
				onError(response.data.error.message);
			} else { //Unexpected error
				console.log('unexepcted error', response);
				onError('Unexpected error: ' + JSON.stringify(response.data));
			}
		} catch (error: unknown) {
			console.log('caught error', error);
			if (axios.isAxiosError(error)) {
				console.log('a');
				if (error.response?.data.error.message) { //Clean error
					console.log('b');
					onError(error.response.data.error.message);
				} else { //Unexpected error
					console.log('c error');
					console.log(error);
					onError(error.message);
				}
			} else if (error instanceof Error) {
				console.log('d');
				onError(error.message);
			} else { //Unexpected error
				console.log('e');
				onError('Unexpected error: ' + JSON.stringify(error));
			}
		} finally {
			if (onFinally) {
				onFinally();
			}
		}
	};

	useEffect(() => {
		setLangCode(lang);

		sendRequest({
			type: 'bizData',
			params: {
				lang: lang,
				stringsToFetch: stringsToFetch
			},
			onSuccess: (data) => {
				if (data.bizData.error && data.bizData.error.message) {
					setBusinessConfigError(data.bizData.error.message);	
				} else { //Success
					console.log(data);
					setIsBusinessLoaded(true);

					let usesAgeGroups = false;

					data.bizData.ageGroups.forEach((ageGroup: AgeGroupDetails) => {
						if (ageGroup.enabled && ageGroup.code !== 'adult') {
							usesAgeGroups = true;
						}
					});

					const activeActivities = data.bizData.tours.filter((tour: Activity) => tour.availableAsTourType.private || tour.availableAsTourType.group);

					const filteredActivities = limitedCategoryIds.length > 0
						? activeActivities.filter((tour: Activity) =>
							tour.categoryId !== null && limitedCategoryIds.includes(tour.categoryId)
						)
						: activeActivities;
					
					setActivities(filteredActivities);
					setActivityAddons(data.bizData.addons);
					setActivityCategories(data.bizData.categories);
					setCurrencyOptions(data.bizData.currencyOptions);
					setCountryOptions(data.bizData.countryOptions);
					setBusinessConfig({
						name: data.bizData.name,
						logo: {
							hasLogo: data.bizData.logo.hasLogo,
							url: data.bizData.logo.url,
							dimensions: {
								width: data.bizData.logo.width,
								height: data.bizData.logo.height
							}
						},
						currency: data.bizData.currency,
						countryCode: data.bizData.countryCode,
						useLodgings: data.bizData.config.useLodgings,
						usePickupLocations: data.bizData.config.usePickupLocations,
						pickupLocations: data.bizData.pickupLocations,
						paxPickup: {
							mode: data.bizData.config.paxPickup.mode,
							useCollectLodging: data.bizData.config.paxPickup.useCollectLodging
						},
						privateTourMinPax: data.bizData.config.privateTourMinPax,
						usesAgeGroups: usesAgeGroups,
						ageGroups: data.bizData.ageGroups,
						clientDataCustom: data.bizData.clientDataCustom,
						clientDataStd: data.bizData.clientDataStd,
						paxListSetup: data.bizData.paxListSetup,
						hideInfo: {
							arrivalDeparture: data.bizData.config.hideInfo.arrivalDeparture,
						},
						languageOptions: data.bizData.languageOptions,
						colors: data.bizData.colors,
						useSeparateCategories: data.bizData.config.webIntegrationsUseSeparateCategories,
						layout: data.bizData.config.webIntegrationsLayout,
						appRootUrlToUse: data.bizData.config.appRootUrlToUse,
						bizContactEmails: data.bizData.bizContactEmails
					});
					setStrings(data.bizData.strings);
					setActivityItems(data.bizData.items);
					setActivityRecommendations(data.bizData.activityRecommendations);

					//Generate activity interval price justments in correct format
					setActivityIntervalPriceAdjustments(
						data.bizData.intervalPriceAdjustments.map((intervalPriceAdjustment: any) => {
							return {
								id: intervalPriceAdjustment.id,
								adjustmentPercentage: intervalPriceAdjustment.adjustmentPercentage,
								dates: {
									from: new Date(`${intervalPriceAdjustment.dateFrom}T00:00:00`),
									to: new Date(`${intervalPriceAdjustment.dateTo}T00:00:00`)
								}
							};
						})
					);

					const { general, users, businesses } = data.bizData.seasons;
					const formattedGeneral = general.map(formatSeason);
				
					const formattedUsers = users ? Object.fromEntries(
						Object.entries(users).map(
							([key, value]) => [key, (value as any[]).map(formatSeason)]
						)
					) : {};
				
					const formattedBusinesses = businesses ? Object.fromEntries(
						Object.entries(businesses).map(
							([key, value]) => [key, (value as any[]).map(formatSeason)]
						)
					) : {};
				
					setActivitySeasons({
						general: formattedGeneral,
						agents: {
							users: formattedUsers,
							businesses: formattedBusinesses
						}
					});
				}
			},
			onError: (error) => {
				setBusinessConfigError(error);
			}
		});
	}, []);

	const formatSeason = (season: any): ActivitySeason => {
		return {
			id: season.id,
			agentId: season.agentId,
			dates: {
				from: new Date(`${season.dateFromYmd}T00:00:00`),
				to: new Date(`${season.dateToYmd}T00:00:00`)
			},
			isBusiness: season.isBusiness,
			prices: season.prices
		};
	};

	const getBizConfigVar = <T extends keyof BusinessConfig>(key: T, defaultValue?: BusinessConfig[T]): BusinessConfig[T] => {
		const value = businessConfig[key];
		if (value === undefined) {
			if (defaultValue !== undefined) {
			return defaultValue;
			}
			throw new Error(`Business config var "${key}" not found`);
		}

		return value;
	};

	const getCurrency = (currencyCode: string) => {
		return currencyOptions.find((currencyOption) => currencyOption.code === currencyCode);
	};

	const getExchangeFactor = (itemCurrencyCode: string, fromCurrencyCode: string|null = null) => {
		if (fromCurrencyCode === null) {
			fromCurrencyCode = getBizConfigVar('currency');
		}
	
		let exchangeFactor = 1;
	
		if (fromCurrencyCode !== itemCurrencyCode) {
			const fromCurrencyObj = getCurrency(fromCurrencyCode);
			const selectedCurrencyObj = getCurrency(itemCurrencyCode);
	
			if (fromCurrencyObj && selectedCurrencyObj) {
				exchangeFactor = fromCurrencyObj.ratePerUSD / selectedCurrencyObj.ratePerUSD;
			}
		}
	
		return exchangeFactor;
	}

	const getActivitySeasons = (agentId?: number, isBusiness?: boolean) => {
		// Filter activitySeasons to include only those with a matching agentId, or include all if no agentId is specified
		if (!activitySeasons) {
			return [];
		}

		if (!agentId) { //Wants only general seasons
			return activitySeasons.general;
		} else { //Wants agent specific seasons
			if (isBusiness) {
				return activitySeasons.agents.businesses[agentId] || [];
			} else {
				return activitySeasons.agents.users[agentId] || [];
			}
		}
	};

	const businessName = getBizConfigVar('name');
	const logo = getBizConfigVar('logo');

	if (businessConfigError) {
		return <PaddingWrap><Alert severity="error">{businessConfigError}</Alert></PaddingWrap>;
	}

	return (
		<>
			<NotEmbeddedHeader
				isEmbedded={isEmbedded}
				isBusinessLoaded={isBusinessLoaded}
				logo={logo}
				businessName={businessName}
				bizContactEmails={getBizConfigVar('bizContactEmails')}
			/>
			<BusinessContext.Provider value={{
				getBizConfigVar,
				activities,
				activityAddons,
				activityCategories,
				activityItems,
				activityIntervalPriceAdjustments,
				getActivitySeasons,
				activityRecommendationsCleaned,
				currencyOptions,
				countryOptions,
				getCurrency,
				getExchangeFactor,
				businessConfig,
				businessConfigError,
				isBusinessLoaded,
				sendRequest,
				setPageTitle,
				businessSlug,
				businessToken
			}}>
				{children}
			</BusinessContext.Provider>
		</>
	);
};

export default BusinessContext;